/**
 * Gobodo Activity Provider
 *
 * Implements activity management using Gobodo GraphObjectsApi for persistence.
 * Activity__c is a custom object type that stores all activities (calls, meetings, tasks, notes).
 * NO localStorage fallback - all operations go through Gobodo API.
 */

import * as GobodoApi from '@gobodo/gobodo-api-promise';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import GobodoConfig, { getCustomSuffix } from '../../config/gobodoConfig';
import { getActivityRelKey } from '../../config/gobodoAccountRelationshipKeys';
import { Activity, ActivityFilters, ActivityType, RecordType, ActivitySource, SourceApp, CallDirection, CallOutcome } from '../../types/activity';
import { serializeGobodoData } from './gobodoRoleProvider';
import { handleGobodoError, logGobodoError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { ensureObjectTypeExists } from '../../utils/gobodoObjectTypeVerifier';
import { shouldUseGobodo } from '../../utils/gobodoUtils';

// Verbose logging in dev so activity-creation failures surface clearly
// (previously hard-coded to false, which masked every cause of
// createActivity failures behind the generic "unexpected error" wrapper).
const DEBUG = process.env.NODE_ENV === 'development';

// Cache to track if object type has been verified this session
let activityTypeVerified = false;
let activityTypeVerifyPromise: Promise<void> | null = null;

function debugLog(message: string, ...args: any[]) {
  if (DEBUG) {
    console.log(`[GobodoActivityProvider] ${message}`, ...args);
  }
}

/**
 * Ensure Activity object type exists (cached - only runs once per session)
 */
async function ensureActivityTypeExists(): Promise<void> {
  if (activityTypeVerified) {
    debugLog('Activity type already verified (cached)');
    return;
  }

  // If verification is already in progress, wait for it
  if (activityTypeVerifyPromise) {
    debugLog('Activity type verification already in progress, waiting...');
    return activityTypeVerifyPromise;
  }

  const activityTypeKey = getActivityTypeKey();
  debugLog(`Verifying Activity object type: ${activityTypeKey}`);

  activityTypeVerifyPromise = ensureObjectTypeExists(activityTypeKey, {
    label: 'Activity',
    pluralLabel: 'Activities',
    description: 'Track all activities (calls, meetings, tasks, notes)',
    icon: 'Activity',
    category: 'custom',
  }).then(() => {
    activityTypeVerified = true;
    debugLog('Activity object type verified and cached');
  }).catch((error) => {
    debugLog('Failed to verify Activity object type:', error);
    activityTypeVerifyPromise = null; // Allow retry on next call
    throw error;
  });

  return activityTypeVerifyPromise;
}

/**
 * Get the Activity object type key using the configured custom suffix
 */
function getActivityTypeKey(): string {
  return `Activity${getCustomSuffix()}`;
}

function stripGraphSuffix(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.replace(/__(stg\d*_c|stg_c|dev_c|stg|c)$/i, '');
}

function toGraphObjectKey(value: string): string {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (/(?:__stg\d*_c|__stg_c|__dev_c|__stg|__c)$/i.test(raw)) return raw;
  return `${raw}${getCustomSuffix()}`;
}

function buildParentLookupCandidates(recordId: string): string[] {
  const raw = String(recordId || '').trim();
  const stripped = stripGraphSuffix(raw);
  return Array.from(
    new Set(
      [raw, toGraphObjectKey(raw), stripped, toGraphObjectKey(stripped)]
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    )
  );
}

async function resolveParentObjectId(
  objectsApi: GobodoApi.GraphObjectsApi,
  recordId: string
): Promise<number | null> {
  const raw = String(recordId || '').trim();
  if (!raw) return null;
  if (/^\d+$/.test(raw)) {
    try {
      const record = await retryWithBackoff(() => objectsApi.getObjectById(Number(raw)), { maxRetries: 1 });
      const numericId = Number(record?.id);
      if (Number.isFinite(numericId)) return numericId;
    } catch {
      // Fall through to key-based lookups below.
    }
  }

  for (const candidate of buildParentLookupCandidates(raw)) {
    try {
      const record = await retryWithBackoff(() => objectsApi.getObjectByKey(candidate), { maxRetries: 1 });
      const numericId = Number(record?.id);
      if (Number.isFinite(numericId)) return numericId;
    } catch {
      // Try the next candidate.
    }
  }
  return null;
}

function normalizeRecordToken(value: unknown): string {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  return raw
    .replace(/__(stg\d*_c|stg_c|stg|c)$/i, '')
    .replace(/\s+/g, '');
}

function tokensMatch(left: unknown, right: unknown): boolean {
  const a = normalizeRecordToken(left);
  const b = normalizeRecordToken(right);
  if (!a || !b) return false;
  return a === b;
}

function addRecordToken(target: Set<string>, value: unknown): void {
  const token = String(value ?? '').trim();
  if (!token) return;
  target.add(token);
}

function matchesAnyRecordToken(value: unknown, candidates: Set<string>): boolean {
  for (const candidate of Array.from(candidates)) {
    if (tokensMatch(value, candidate)) return true;
  }
  return false;
}

function normalizeRecordType(value: unknown): RecordType | '' {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  if (raw === 'account') return 'client';
  if (raw === 'oppty') return 'opportunity';
  return raw as RecordType;
}

function objectTypeMatches(typeKey: string, expected: string): boolean {
  const left = String(typeKey || '').toLowerCase();
  const right = expected.toLowerCase();
  return left === right || left.startsWith(right + '__');
}

function getObjectTypeKey(obj: any): string {
  return String(
    obj?.objecttype?.key ||
    obj?.objectType?.key ||
    obj?.typekey ||
    obj?.typeKey ||
    ''
  );
}

function getPayloadProps(serialized: any): Record<string, any> {
  const props = serialized?.properties;
  const data = serialized?.data;
  if (props && typeof props === 'object' && Object.keys(props).length > 0) return props;
  if (data && typeof data === 'object' && Object.keys(data).length > 0) return data;
  return serialized && typeof serialized === 'object' ? serialized : {};
}

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number, label: string): Promise<T> {
  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(`${label} timed out after ${timeoutMs}ms`)), timeoutMs);
  });
  try {
    return await Promise.race([promise, timeoutPromise]);
  } finally {
    if (timeoutHandle) clearTimeout(timeoutHandle);
  }
}

function pickProp(props: Record<string, any>, candidates: string[]): string {
  for (const key of candidates) {
    const direct = props[key];
    if (direct != null && String(direct).trim()) return String(direct);
    const lower = props[key.toLowerCase()];
    if (lower != null && String(lower).trim()) return String(lower);
  }
  return '';
}

function deriveRecordBinding(props: Record<string, any>): { recordId: string; recordType: RecordType } {
  const caseId = pickProp(props, [
    'RecordId',
    'record_id',
    'WhatId__c',
    'WhatId',
    'ParentId__c',
    'ParentId',
    'case_id',
    'CaseId__c',
    'custom__case',
    'related_to_id',
  ]);
  if (caseId) return { recordId: caseId, recordType: 'case' };
  const clientId = pickProp(props, [
    'AccountId__c',
    'AccountId',
    'account_id',
    'custom__client',
    'WhoId__c',
    'WhoId',
  ]);
  if (clientId) return { recordId: clientId, recordType: 'client' };
  return { recordId: '', recordType: 'client' };
}

function convertFromNonActivityObject(gobodoObject: any, fallbackType: ActivityType): Activity {
  const serialized = serializeGobodoData(gobodoObject);
  const suffix = getCustomSuffix();
  const props = getPayloadProps(serialized);
  const getProp = (...names: string[]) => {
    for (const name of names) {
      const withSuffix = props[`${name}${suffix}`];
      if (withSuffix != null && String(withSuffix).trim() !== '') return withSuffix;
      const custom = props[`${name}__c`];
      if (custom != null && String(custom).trim() !== '') return custom;
      const lower = props[String(name).toLowerCase()];
      if (lower != null && String(lower).trim() !== '') return lower;
    }
    return undefined;
  };

  const binding = deriveRecordBinding(props);
  const createdAt = String(
    getProp('CreatedAt', 'created_date', 'CreatedDate', 'ActivityDate') ||
    serialized.creationdate ||
    new Date().toISOString()
  );
  const subject = String(getProp('Subject', 'subject', 'Name', 'name', 'Field') || '').trim() || undefined;
  const description = String(
    getProp('Description', 'description', 'TextPreview', 'old_value', 'new_value') || ''
  ).trim() || undefined;

  return {
    id: serialized.id?.toString() || String(serialized.id || ''),
    type: fallbackType,
    recordId: binding.recordId,
    recordType: binding.recordType,
    userId: String(getProp('UserId', 'owner_id', 'created_by_id') || ''),
    userName: String(getProp('UserName', 'owner_name', 'created_by_name') || ''),
    createdAt,
    subject,
    description,
    source: 'manual',
    sourceApp: 'credit_block',
  };
}

/**
 * Convert Gobodo GraphObject to our Activity interface
 * Handles both suffixed (e.g., Type__stg) and non-suffixed property names
 */
function convertFromGobodoActivity(gobodoActivity: any): Activity {
  try {
    // Serialize the entire Gobodo response to convert wrapped Gobodo values
    const serialized = serializeGobodoData(gobodoActivity);
    const suffix = getCustomSuffix();

    // Extract fields from properties (Gobodo stores custom fields in properties)
    const props = getPayloadProps(serialized);

    // Helper to get property with suffix fallback
    const getProp = (name: string) => props[`${name}${suffix}`] || props[`${name}__c`] || props[name.toLowerCase()];

    const durationVal = getProp('Duration');
    const parsedDuration = durationVal ? parseInt(durationVal, 10) : undefined;

    return {
      id: serialized.id?.toString() || String(serialized.id || ''),
      type: (getProp('Type') || 'note') as ActivityType,
      recordId: getProp('RecordId') || '',
      recordType: (getProp('RecordType') || 'client') as RecordType,
      userId: getProp('UserId') || '',
      userName: getProp('UserName') || '',
      createdAt: getProp('CreatedAt') || serialized.creationdate || new Date().toISOString(),
      subject: getProp('Subject') || undefined,
      description: getProp('Description') || undefined,
      duration: !isNaN(parsedDuration as number) ? parsedDuration : undefined,
      direction: getProp('Direction') as CallDirection | undefined,
      outcome: getProp('Outcome') as CallOutcome | undefined,
      scheduledAt: getProp('ScheduledAt') || undefined,
      timezone: getProp('Timezone') || undefined,
      location: getProp('Location') || undefined,
      recipientEmail: getProp('RecipientEmail') || undefined,
      recipientPhone: getProp('RecipientPhone') || undefined,
      recipientName: getProp('RecipientName') || undefined,
      templateId: getProp('TemplateId') || undefined,
      templateName: getProp('TemplateName') || undefined,
      source: (getProp('Source') || 'manual') as ActivitySource,
      sourceApp: getProp('SourceApp') as SourceApp | undefined,
    };
  } catch (error) {
    console.error('[GobodoActivityProvider] Error converting activity:', error, gobodoActivity);
    // Return a minimal activity object to prevent breaking the app
    return {
      id: gobodoActivity?.id?.toString() || String(gobodoActivity?.id || 'unknown'),
      type: 'note',
      recordId: '',
      recordType: 'client',
      userId: '',
      userName: '',
      createdAt: new Date().toISOString(),
      source: 'manual',
    };
  }
}

/**
 * Convert our Activity to Gobodo properties object
 * Uses custom suffix from config (e.g., __stg, __c)
 */
function activityToGobodoProps(activity: Partial<Activity>): Record<string, any> {
  const props: Record<string, any> = {};
  const suffix = getCustomSuffix();

  if (activity.type !== undefined) props[`Type${suffix}`] = activity.type;
  if (activity.subject !== undefined) props[`Subject${suffix}`] = activity.subject;
  if (activity.description !== undefined) props[`Description${suffix}`] = activity.description;
  if (activity.recordId !== undefined) props[`RecordId${suffix}`] = activity.recordId;
  if (activity.recordType !== undefined) props[`RecordType${suffix}`] = activity.recordType;
  if (activity.userId !== undefined) props[`UserId${suffix}`] = activity.userId;
  if (activity.userName !== undefined) props[`UserName${suffix}`] = activity.userName;
  if (activity.createdAt !== undefined) props[`CreatedAt${suffix}`] = activity.createdAt;
  if (activity.scheduledAt !== undefined) props[`ScheduledAt${suffix}`] = activity.scheduledAt;
  if (activity.timezone !== undefined) props[`Timezone${suffix}`] = activity.timezone;
  if (activity.duration !== undefined) props[`Duration${suffix}`] = activity.duration;
  if (activity.location !== undefined) props[`Location${suffix}`] = activity.location;
  if (activity.direction !== undefined) props[`Direction${suffix}`] = activity.direction;
  if (activity.outcome !== undefined) props[`Outcome${suffix}`] = activity.outcome;
  if (activity.recipientEmail !== undefined) props[`RecipientEmail${suffix}`] = activity.recipientEmail;
  if (activity.recipientPhone !== undefined) props[`RecipientPhone${suffix}`] = activity.recipientPhone;
  if (activity.recipientName !== undefined) props[`RecipientName${suffix}`] = activity.recipientName;
  if (activity.source !== undefined) props[`Source${suffix}`] = activity.source;
  if (activity.sourceApp !== undefined) props[`SourceApp${suffix}`] = activity.sourceApp;
  if (activity.templateId !== undefined) props[`TemplateId${suffix}`] = activity.templateId;
  if (activity.templateName !== undefined) props[`TemplateName${suffix}`] = activity.templateName;

  return props;
}

/**
 * Gobodo Activity Provider Implementation
 *
 * Connects to Gobodo GraphObjectsApi for activity CRUD operations.
 * NO localStorage fallback - all operations require Gobodo API.
 */
export class GobodoActivityProvider {
  /**
   * Get all activities with optional filtering
   */
  async getActivities(filters?: ActivityFilters): Promise<Activity[]> {
    debugLog('getActivities called with filters:', filters);

    // Check if Gobodo is configured
    if (!shouldUseGobodo()) {
      debugLog('ERROR: Gobodo API is not enabled. Set REACT_APP_USE_GOBODO_API=true');
      console.error('[GobodoActivityProvider] Gobodo API is not enabled. Activities will not load.');
      console.error('[GobodoActivityProvider] Set REACT_APP_USE_GOBODO_API=true in your .env.local file');
      return []; // Return empty array instead of throwing to prevent app crash
    }

    try {
      debugLog('Getting Gobodo client...');
      const client = getGobodoClient();
      debugLog('Client obtained:', !!client);

      const objectsApi = new GobodoApi.GraphObjectsApi(client);
      const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

      // Pull native Activity plus synced SF activity-like objects.
      const graphId = GobodoConfig.graph();
      const suffix = getCustomSuffix();
      const sourceTypeKeys: Array<{ key: string; fallbackType: ActivityType }> = [
        { key: getActivityTypeKey(), fallbackType: 'note' },
        { key: `Task${suffix}`, fallbackType: 'task' },
        { key: `Event${suffix}`, fallbackType: 'meeting' },
        { key: `EmailMessage${suffix}`, fallbackType: 'email' },
        { key: `CaseHistory${suffix}`, fallbackType: 'note' },
        { key: `Note${suffix}`, fallbackType: 'note' },
      ];

      try {
        await withTimeout(ensureActivityTypeExists(), 4000, 'ensureActivityTypeExists');
      } catch (error) {
        debugLog('Activity type verification timed out, continuing with partial sources', error);
      }

      const filterRecordType = normalizeRecordType(filters?.recordType);
      const recordIdCandidates = new Set<string>();
      addRecordToken(recordIdCandidates, filters?.recordId);

      if (filterRecordType === 'case' && filters?.recordId) {
        try {
          const raw = String(filters.recordId).trim();
          const lookup = /^\d+$/.test(raw)
            ? await withTimeout(objectsApi.getObjectById(Number(raw)), 4500, `getObjectById(${raw})`)
            : await withTimeout(objectsApi.getObjectByKey(raw), 4500, `getObjectByKey(${raw})`);
          const serialized = serializeGobodoData(lookup);
          const props = getPayloadProps(serialized);
          addRecordToken(recordIdCandidates, serialized?.id);
          addRecordToken(recordIdCandidates, serialized?.key);
          addRecordToken(recordIdCandidates, props?.sf_id);
          addRecordToken(recordIdCandidates, props?.CaseId__c);
          addRecordToken(recordIdCandidates, props?.case_id);
          addRecordToken(recordIdCandidates, props?.RecordId__c);
          addRecordToken(recordIdCandidates, props?.record_id);
        } catch (error) {
          debugLog('Case record alias lookup failed, proceeding with raw recordId only', error);
        }
      }
      if (filterRecordType === 'client' && filters?.recordId) {
        try {
          const raw = String(filters.recordId).trim();
          const lookup = /^\d+$/.test(raw)
            ? await withTimeout(objectsApi.getObjectById(Number(raw)), 4500, `getObjectById(${raw})`)
            : await withTimeout(objectsApi.getObjectByKey(raw), 4500, `getObjectByKey(${raw})`);
          const serialized = serializeGobodoData(lookup);
          const props = getPayloadProps(serialized);
          addRecordToken(recordIdCandidates, serialized?.id);
          addRecordToken(recordIdCandidates, serialized?.key);
          addRecordToken(recordIdCandidates, props?.sf_id);
          addRecordToken(recordIdCandidates, props?.AccountId__c);
          addRecordToken(recordIdCandidates, props?.account_id);
          addRecordToken(recordIdCandidates, props?.ClientId__c);
          addRecordToken(recordIdCandidates, props?.client_id);
          addRecordToken(recordIdCandidates, props?.custom__client);
          addRecordToken(recordIdCandidates, props?.recordid__c);
        } catch (error) {
          debugLog('Client record alias lookup failed, proceeding with raw recordId only', error);
        }
      }

      const allRows = (
        await Promise.all(
          sourceTypeKeys.map(async (source) => {
            try {
              await withTimeout(
                retryWithBackoff(
                  () => objectTypesApi.getObjectTypeByKey(source.key, { maxFields: 0 }),
                  { maxRetries: 1 }
                ),
                4500,
                `getObjectTypeByKey(${source.key})`
              );
            } catch (error: any) {
              const status = error?.status ?? error?.response?.status;
              if (status === 404) {
                debugLog(`Skipping missing object type ${source.key}`);
                return [] as Array<{ row: any; source: { key: string; fallbackType: ActivityType } }>;
              }
              debugLog(`Skipping source ${source.key} due type lookup error`, error);
              return [] as Array<{ row: any; source: { key: string; fallbackType: ActivityType } }>;
            }

            let response: unknown;
            try {
              response = await withTimeout(
                retryWithBackoff(
                  () => objectsApi.getObjectsInGraphByType(graphId, source.key, { page: 0, size: 1000 }),
                  { maxRetries: 1 }
                ),
                6500,
                `getObjectsInGraphByType(${source.key})`
              );
            } catch (error) {
              debugLog(`Skipping source ${source.key} due object fetch error`, error);
              return [] as Array<{ row: any; source: { key: string; fallbackType: ActivityType } }>;
            }

            if (!Array.isArray(response) || response.length === 0) {
              return [] as Array<{ row: any; source: { key: string; fallbackType: ActivityType } }>;
            }
            return response.map((row) => ({ row, source }));
          })
        )
      ).flat();

      // Convert to Activity objects
      const activities: Activity[] = [];
      for (const wrapped of allRows) {
        const gobodoActivity = wrapped.row;
        const objectTypeKey = getObjectTypeKey(serializeGobodoData(gobodoActivity));
        try {
          const activity =
            objectTypeMatches(objectTypeKey, 'Activity')
              ? convertFromGobodoActivity(gobodoActivity)
              : convertFromNonActivityObject(gobodoActivity, wrapped.source.fallbackType);
          if (activity.id) {
            activities.push(activity);
          }
        } catch (error) {
          const gobodoError = handleGobodoError(error as any, { operation: 'getActivities' });
          if (gobodoError) {
            logGobodoError(gobodoError, 'warn');
          }
        }
      }

      // Apply filters
      let filtered = activities;

      if (filters?.recordId) {
        filtered = filtered.filter((a) =>
          recordIdCandidates.size > 0
            ? matchesAnyRecordToken(a.recordId, recordIdCandidates)
            : tokensMatch(a.recordId, filters.recordId)
        );
      }
      if (filters?.recordType) {
        filtered = filtered.filter((a) => normalizeRecordType(a.recordType) === filterRecordType);
      }
      if (filters?.userId) {
        filtered = filtered.filter(a => a.userId === filters.userId);
      }
      if (filters?.userIds && filters.userIds.length > 0) {
        filtered = filtered.filter(a => filters.userIds!.includes(a.userId));
      }
      if (filters?.types && filters.types.length > 0) {
        filtered = filtered.filter(a => filters.types!.includes(a.type));
      }
      if (filters?.dateFrom) {
        filtered = filtered.filter(a => a.createdAt >= filters.dateFrom!);
      }
      if (filters?.dateTo) {
        filtered = filtered.filter(a => a.createdAt <= filters.dateTo!);
      }
      if (filters?.source) {
        filtered = filtered.filter(a => a.source === filters.source);
      }
      if (filters?.sourceApps && filters.sourceApps.length > 0) {
        filtered = filtered.filter(a => a.sourceApp && filters.sourceApps!.includes(a.sourceApp));
      }

      // Sort by createdAt descending (newest first)
      return filtered.sort((a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error: any) {
      debugLog('ERROR in getActivities:', error);
      debugLog('Error details:', {
        message: error?.message,
        status: error?.response?.status || error?.status,
        data: error?.response?.data,
      });

      const gobodoError = handleGobodoError(error, { operation: 'getActivities' });
      if (gobodoError) {
        logGobodoError(gobodoError);
        // Return empty array instead of throwing to prevent app crash
        console.error('[GobodoActivityProvider] Failed to fetch activities:', gobodoError.message);
        return [];
      }
      console.error('[GobodoActivityProvider] Unexpected error:', error);
      return [];
    }
  }

  /**
   * Get activities for a specific record
   */
  async getByRecord(recordId: string, recordType: RecordType): Promise<Activity[]> {
    return this.getActivities({ recordId, recordType });
  }

  /**
   * Get activities by user
   */
  async getByUser(userId: string, dateFrom?: string, dateTo?: string): Promise<Activity[]> {
    return this.getActivities({ userId, dateFrom, dateTo });
  }

  /**
   * Get single activity by ID
   */
  async getActivityById(id: string): Promise<Activity | null> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        // Try to find by string ID in the activities list
        const activities = await this.getActivities();
        return activities.find(a => a.id === id) || null;
      }

      const response = await retryWithBackoff(
        () => objectsApi.getObjectById(numericId),
        { maxRetries: 3, initialDelayMs: 1500 }
      );

      if (!response) {
        return null;
      }

      return convertFromGobodoActivity(response);
    } catch (error: any) {
      if (error.response?.status === 404 || error.status === 404) {
        return null;
      }
      const gobodoError = handleGobodoError(error, { operation: 'getActivityById', activityId: id });
      if (gobodoError) {
        logGobodoError(gobodoError, 'warn');
        throw gobodoError;
      }
      throw error;
    }
  }

  /**
   * Create a new activity
   */
  async createActivity(activity: Omit<Activity, 'id'>): Promise<Activity> {
    debugLog('createActivity called with:', activity);

    // Check if Gobodo is configured
    if (!shouldUseGobodo()) {
      debugLog('ERROR: Gobodo API is not enabled. Cannot create activity.');
      throw new Error('Gobodo API is not enabled. Set REACT_APP_USE_GOBODO_API=true');
    }

    try {
      debugLog('Getting Gobodo client for create...');
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);
      const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

      const activityTypeKey = getActivityTypeKey();
      const graphId = GobodoConfig.graph();
      debugLog(`Creating activity with type ${activityTypeKey} in graph ${graphId}`);

      // Ensure Activity object type exists (cached)
      await ensureActivityTypeExists();

      // Get object type to get the type ID
      const objectType = await retryWithBackoff(
        () => objectTypesApi.getObjectTypeByKey(activityTypeKey, { maxFields: 0 }),
        { maxRetries: 3 }
      );

      // Set createdAt if not provided
      const activityWithTimestamp = {
        ...activity,
        createdAt: activity.createdAt || new Date().toISOString(),
      };

      // Build props from activity
      const props = activityToGobodoProps(activityWithTimestamp);

      // Build payload
      const payload = new GobodoApi.GraphObjectPayload();
      payload.typekey = activityTypeKey;
      payload.typeid = objectType.id as any;
      payload.label = activity.subject || `${activity.type} - ${new Date().toISOString()}`;
      payload.graphs = [graphId];
      payload.properties = props;
      const parentObjectId = await resolveParentObjectId(objectsApi, activity.recordId);
      if (parentObjectId != null) {
        payload.parentobjectid = parentObjectId;
        payload.relationshiptypekey = getActivityRelKey();
      } else {
        debugLog('Could not resolve parent object id for activity recordId', activity.recordId);
      }

      // Warmup ping: the first createObject after Gobodo idles tends to
      // 504 (cold start). Issue a cheap GET first to wake the backend so
      // the actual write lands on a warm connection. Failures are ignored
      // — this is best-effort.
      try {
        await objectsApi.getObjectsInGraphByType(graphId, activityTypeKey, {
          page: 0,
          size: 1,
          maxObjectTypeFields: 0,
        });
      } catch (warmupError) {
        debugLog('Warmup ping failed (continuing to createObject anyway):', warmupError);
      }

      // Higher retry budget for the actual write — 504s have been observed
      // on cold-start. 5 retries with 2s initial backoff (with jitter)
      // gives roughly 60s of recovery window for transient gateway timeouts.
      const response = await retryWithBackoff(
        () => objectsApi.createObject(payload),
        { maxRetries: 5, initialDelayMs: 2000, maxDelayMs: 15_000 }
      );

      debugLog('Activity created successfully:', response);
      return convertFromGobodoActivity(response);
    } catch (error: any) {
      // Always log the raw error in dev — the generic GobodoError wrapper
      // below otherwise drops the real cause on the floor.
      if (process.env.NODE_ENV === 'development') {
        console.error('[GobodoActivityProvider] createActivity raw error:', {
          message: error?.message,
          status: error?.response?.status || error?.status,
          body: error?.response?.text || error?.response?.body,
          stack: error?.stack,
          full: error,
        });
      }
      debugLog('ERROR in createActivity:', error);
      debugLog('Error details:', {
        message: error?.message,
        status: error?.response?.status || error?.status,
        data: error?.response?.data,
      });

      const gobodoError = handleGobodoError(error, { operation: 'createActivity' });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }
  }

  /**
   * Update an existing activity
   */
  async updateActivity(id: string, updates: Partial<Activity>): Promise<Activity> {
    debugLog('updateActivity called with id:', id, 'updates:', updates);

    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);
      const objectTypesApi = new GobodoApi.GraphObjectTypesApi(client);

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        throw new Error(`Invalid activity ID format for update: ${id}`);
      }

      // Get existing activity to merge updates
      const existingActivity = await this.getActivityById(id);
      if (!existingActivity) {
        throw new Error(`Activity with ID ${id} not found`);
      }

      const activityTypeKey = getActivityTypeKey();
      const graphId = GobodoConfig.graph();

      // Get object type to get the type ID
      const objectType = await retryWithBackoff(
        () => objectTypesApi.getObjectTypeByKey(activityTypeKey, { maxFields: 0 }),
        { maxRetries: 3 }
      );

      // Merge updates
      const updatedActivity = { ...existingActivity, ...updates };
      const props = activityToGobodoProps(updatedActivity);

      // Build payload
      const payload = new GobodoApi.GraphObjectPayload();
      payload.typekey = activityTypeKey;
      payload.typeid = objectType.id as any;
      payload.label = updatedActivity.subject || `${updatedActivity.type} - ${updatedActivity.createdAt}`;
      payload.graphs = [graphId];
      payload.properties = props;

      const response = await retryWithBackoff(
        () => objectsApi.updateObject(numericId, payload),
        { maxRetries: 3 }
      );

      if (!response) {
        throw new Error('Update response was empty');
      }

      return convertFromGobodoActivity(response);
    } catch (error: any) {
      const gobodoError = handleGobodoError(error, { operation: 'updateActivity', activityId: id });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }
  }

  /**
   * Delete an activity
   */
  async deleteActivity(id: string): Promise<boolean> {
    try {
      const client = getGobodoClient();
      const objectsApi = new GobodoApi.GraphObjectsApi(client);

      const numericId = parseInt(id, 10);
      if (isNaN(numericId)) {
        throw new Error(`Invalid activity ID format for delete: ${id}`);
      }

      await retryWithBackoff(
        () => objectsApi.deleteObject(numericId),
        { maxRetries: 2 }
      );

      return true;
    } catch (error: any) {
      if (error.response?.status === 404 || error.status === 404) {
        return false; // Already deleted
      }
      const gobodoError = handleGobodoError(error, { operation: 'deleteActivity', activityId: id });
      if (gobodoError) {
        logGobodoError(gobodoError);
        throw gobodoError;
      }
      throw error;
    }
  }

  /**
   * Analytics: Count activities by type for a user within date range
   */
  async getCountByType(userId: string, dateFrom: string, dateTo: string): Promise<Record<ActivityType, number>> {
    const activities = await this.getByUser(userId, dateFrom, dateTo);

    const counts: Record<ActivityType, number> = {
      email: 0,
      call: 0,
      sms: 0,
      meeting: 0,
      task: 0,
      note: 0,
    };

    activities.forEach(a => {
      counts[a.type]++;
    });

    return counts;
  }

  /**
   * Analytics: Get daily activity count for a user
   */
  async getDailyActivityCount(userId: string, date: string): Promise<number> {
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);
    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const activities = await this.getByUser(
      userId,
      startOfDay.toISOString(),
      endOfDay.toISOString()
    );

    return activities.length;
  }
}

// Export singleton instance
export const gobodoActivityProvider = new GobodoActivityProvider();

// Export factory for custom configuration
export function createGobodoActivityProvider(): GobodoActivityProvider {
  return new GobodoActivityProvider();
}
